
import {
  useEffect,
  useState,
  useCallback,
  Component,
	type FC,
  type PropsWithChildren,
	type ReactNode,
} from "react";
import { getAccount, getConnectors } from "wagmi/actions";
import { wagmiConfig } from "@config";
import { ErrorDisplay } from "./ErrorDisplay";

export type ErrorBoundryProps = PropsWithChildren<{
  devmode?: boolean;
  fallback?: ReactNode;
}>;

const sendErrorReport = (
  error: Error | PromiseRejectionEvent,
  errorType: string
) => {
  const { address, connector } = getAccount(wagmiConfig);
  const connectors = getConnectors(wagmiConfig);
  const usedWalletId = connector?.id;
  const usedWalletName = connector?.name;
  const usedWalletAddress = address;
  const browserConnectors = connectors.map((c) => ({
    name: c.name,
    id: c.id,
  }));
  const userAgent = navigator.userAgent;
  const message =
    error instanceof Error
      ? error.message
      : typeof error.reason === "string"
      ? error.reason
      : JSON.stringify(error.reason || {});

  const report = {
    errorType,
    usedWalletId,
    usedWalletName,
    usedWalletAddress,
    browserConnectors,
    userAgent,
    message,
  };

  fetch("/crash", {
    method: "POST",
    body: JSON.stringify(report),
    headers: {
      "Content-Type": "application/json",
    },
  }).then(() => console.log("crash report filed", report));
};

const ignoreKnown = [
  "Cannot read properties of undefined (reading 'code')", // okx wallet bug
  "Failed to execute 'transaction' on 'IDBDatabase': One of the specified object stores was not found.",
  "The requested method and/or account has not been authorized by the user.",
  "has not been authorized yet",
];

export const PromisedErrorBoundry: FC<ErrorBoundryProps> = ({
  children,
  devmode = globalThis.location.host.split(":")[0] === "localhost",
  fallback,
}) => {
  const [error, setError] = useState<Error | null>(null);

  const promiseRejectionHandler = useCallback(
    (event: PromiseRejectionEvent) => {
      if (
        ignoreKnown.some((err) =>
          JSON.stringify(event?.reason || {}).includes(err)
        )
      ) {
        console.log("Ignore whitelisted error", event?.reason);
        return;
      }

      sendErrorReport(event, "promise-rejection");
      setError(
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason))
      );
    },
    []
  );

  useEffect(() => {
    window.addEventListener("unhandledrejection", promiseRejectionHandler);
    return () => {
      window.removeEventListener("unhandledrejection", promiseRejectionHandler);
    };
  }, [promiseRejectionHandler]);

  if (error && !ignoreKnown.some((err) => error.message.includes(err))) {
    if (devmode) {
      return (
        fallback || (
          <ErrorDisplay error={error} onDismiss={() => setError(null)} />
        )
      );
    }
    console.error("ERROR boundary error:", error);
    return (
      fallback || (
				<ErrorDisplay error={error} onDismiss={() => setError(null)} />
      )
    );
  }

  return <>{children}</>;
};

interface ErrorBoundryState {
  hasError: boolean;
  error: Error | null;
  devmode: boolean;
}

export class ErrorBoundry extends Component<
  ErrorBoundryProps,
  ErrorBoundryState
> {
  constructor(props: ErrorBoundryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      devmode: globalThis.location.host.split(":")[0] === "localhost",
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundryState> {
    sendErrorReport(error, "error-global");
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        this.props.fallback || (
          <ErrorDisplay
            error={this.state.error}
            onDismiss={() => this.setState({ hasError: false, error: null })}
          />
        )
      );
    }
    return (
      <PromisedErrorBoundry
        devmode={this.props.devmode}
        fallback={this.props.fallback}
      >
        {this.props.children}
      </PromisedErrorBoundry>
    );
  }
}
